import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useBoundStore } from '@/store';
import { createEmptyRegionConfig, loadDatacenterConfig, selectDatacenter } from '@/utils/datacenter';
import type { Auth0IdTokenClaims } from '@/types/auth';
import type { Region } from '@/constants/regions';

/**
 * Hook to initialize datacenter configuration on app startup.
 * Performs an ATOMIC initialization of:
 * - region
 * - all region-dependent URLs
 *
 * No partial updates.
 * No races.
 */
export function useDatacenterInit(queryParamDatacenter?: string | null) {
  const { getIdTokenClaims, isAuthenticated, isLoading } = useAuth0();
  const { selectedRegion, initializeRegion, hasHydrated } = useBoundStore();

  useEffect(() => {
    let isMounted = true;

    async function initializeDatacenter() {
      if (isLoading) return;
      if (!isMounted) return;

      // Wait for persisted store hydration unless query param is provided
      if (!hasHydrated && !queryParamDatacenter) {
        return;
      }

      try {
        const config = loadDatacenterConfig();

        // Determine allowed datacenters from Auth0 claims
        let userDatacenters: Region[] | undefined;

        if (isAuthenticated) {
          try {
            const claims = (await getIdTokenClaims()) as Auth0IdTokenClaims;

            if (!isMounted) return;

            userDatacenters = claims?.datacenters;
          } catch (error) {
            console.error('[Datacenter] Failed to fetch ID token claims:', error);
          }
        }

        const newSelectedRegion = selectDatacenter(
          queryParamDatacenter,
          userDatacenters,
          config.defaultDatacenter,
          selectedRegion,
        );

        initializeRegion({
          selectedRegion: newSelectedRegion,
          apiGatewayUrls: config.apiGatewayUrls,
          unifiedPlatformApiUrls: config.unifiedPlatformApiUrls,
          thoughtspotUrls: config.thoughtspotUrls,
          surveyUrls: config.surveyUrls,
          digitalUrls: config.digitalUrls,
          workflowUrls: config.workflowUrls,
          pulseUrls: config.pulseUrls,
          reputationUrls: config.reputationUrls,
          listingsUrls: config.listingsUrls,
          digitalMarketingUrls: config.digitalMarketingUrls,
          pulseMarketingUrls: config.pulseMarketingUrls,
          reputationMarketingUrls: config.reputationMarketingUrls,
          listingsMarketingUrls: config.listingsMarketingUrls,
        });
      } catch (error) {
        console.error('[DatacenterInit] initialization failed, falling back', error);

        initializeRegion({
          selectedRegion,
          apiGatewayUrls: createEmptyRegionConfig(),
          unifiedPlatformApiUrls: createEmptyRegionConfig(),
          thoughtspotUrls: createEmptyRegionConfig(),
          surveyUrls: createEmptyRegionConfig(),
          digitalUrls: createEmptyRegionConfig(),
          workflowUrls: createEmptyRegionConfig(),
          pulseUrls: createEmptyRegionConfig(),
          reputationUrls: createEmptyRegionConfig(),
          listingsUrls: createEmptyRegionConfig(),
          digitalMarketingUrls: createEmptyRegionConfig(),
          pulseMarketingUrls: createEmptyRegionConfig(),
          reputationMarketingUrls: createEmptyRegionConfig(),
          listingsMarketingUrls: createEmptyRegionConfig(),
        });
      }
    }

    void initializeDatacenter();

    return () => {
      isMounted = false;
    };
  }, [
    isLoading,
    isAuthenticated,
    queryParamDatacenter,
    getIdTokenClaims,
    hasHydrated,
    selectedRegion,
    initializeRegion,
  ]);
}
